import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Send } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function InquiryForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_location: "", guest_count: 50, taps_needed: 4,
    beverage_type: "beer", event_type: "wedding", message: "",
  });
  const [eventDate, setEventDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Listen for quote calculator prefill
  useEffect(() => {
    const applyPrefill = () => {
      const raw = sessionStorage.getItem("trailhead_prefill");
      if (!raw) return;
      try {
        const p = JSON.parse(raw);
        setForm((f) => ({ ...f, ...p }));
        sessionStorage.removeItem("trailhead_prefill");
        toast.success("Quote locked in — finish the details below");
      } catch {}
    };
    applyPrefill();
    window.addEventListener("trailhead-prefill", applyPrefill);
    return () => window.removeEventListener("trailhead-prefill", applyPrefill);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!eventDate) { toast.error("Please pick an event date"); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        guest_count: Number(form.guest_count),
        taps_needed: Number(form.taps_needed),
        event_date: format(eventDate, "yyyy-MM-dd"),
      });
      toast.success("Inquiry sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", event_location: "", guest_count: 50, taps_needed: 2, beverage_type: "beer", event_type: "wedding", message: "" });
      setEventDate(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="paper-card p-6 md:p-10 space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Name</Label>
          <Input data-testid="inquiry-name" required value={form.name} onChange={update("name")} placeholder="Your full name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Email</Label>
          <Input data-testid="inquiry-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@email.com" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Phone (optional)</Label>
          <Input data-testid="inquiry-phone" value={form.phone} onChange={update("phone")} placeholder="555-555-5555" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Event Type</Label>
          <Select value={form.event_type} onValueChange={update("event_type")}>
            <SelectTrigger data-testid="inquiry-event-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="wedding">Wedding</SelectItem>
              <SelectItem value="corporate">Corporate</SelectItem>
              <SelectItem value="birthday">Birthday / Anniversary</SelectItem>
              <SelectItem value="festival">Festival</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Event Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" data-testid="inquiry-event-date" className="w-full justify-start font-normal border-[#dcd3c6] bg-white">
                <CalendarIcon className="mr-2 h-4 w-4 text-[#c97b3a]" />
                {eventDate ? format(eventDate, "PPP") : <span className="text-[#8c8577]">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={eventDate} onSelect={setEventDate} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Event Location</Label>
          <Input data-testid="inquiry-event-location" required value={form.event_location} onChange={update("event_location")} placeholder="City, venue, or address" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Guest Count</Label>
          <Input data-testid="inquiry-guest-count" type="number" min="1" required value={form.guest_count} onChange={update("guest_count")} />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Taps Needed</Label>
          <Select value={String(form.taps_needed)} onValueChange={(v) => setForm({ ...form, taps_needed: Number(v) })}>
            <SelectTrigger data-testid="inquiry-taps-needed"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 tap</SelectItem>
              <SelectItem value="2">2 taps</SelectItem>
              <SelectItem value="3">3 taps</SelectItem>
              <SelectItem value="4">4 taps</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Beverage Type</Label>
          <Select value={form.beverage_type} onValueChange={update("beverage_type")}>
            <SelectTrigger data-testid="inquiry-beverage-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="beer">Beer (kegs)</SelectItem>
              <SelectItem value="wine">Wine</SelectItem>
              <SelectItem value="cocktails">Cocktails</SelectItem>
              <SelectItem value="mixed">Mixed (beer + wine / cocktails)</SelectItem>
              <SelectItem value="non-alcoholic">Non-alcoholic (cold brew, kombucha)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-[#5a5245] mb-2 block">Anything else? (optional)</Label>
          <Textarea data-testid="inquiry-message" value={form.message} onChange={update("message")} placeholder="Tell us about your vibe, venue, special requests..." rows={4} />
        </div>
      </div>
      <Button type="submit" data-testid="inquiry-submit" disabled={submitting} className="btn-orange w-full md:w-auto rounded-full px-8 py-6 text-base">
        <Send className="mr-2 h-4 w-4" />
        {submitting ? "Sending..." : "Send Inquiry"}
      </Button>
    </form>
  );
}
